import React from 'react';

/**
 * Cross-cutting flag for transient app-shell chrome (currently used by
 * the floating draggable settings menu) that some full-screen flows
 * need to suppress while still rendering normally everywhere else.
 *
 * The only flow that suppresses the floating menu today is the live
 * camera viewfinder in the grading workflow (`CameraScanStep`). Every
 * other screen — including the other steps of the same workflow —
 * leaves the menu visible so the teacher can always reach settings.
 *
 * The flag is intentionally scoped to "transient overlays" only: it
 * has no business-logic side effects and is reset to `false` when the
 * suppressing component unmounts.
 */
export interface FloatingChromeContextValue {
  /** True while a full-screen flow asks the app shell to hide chrome. */
  suppressFloatingMenu: boolean;
  setSuppressFloatingMenu: (suppress: boolean) => void;
}

const noop = () => {
  /* default no-op so consumers can render outside the provider in tests */
};

export const FloatingChromeContext = React.createContext<FloatingChromeContextValue>({
  suppressFloatingMenu: false,
  setSuppressFloatingMenu: noop,
});

export function FloatingChromeProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [suppressFloatingMenu, setSuppressFloatingMenu] = React.useState(false);
  const value = React.useMemo<FloatingChromeContextValue>(
    () => ({ suppressFloatingMenu, setSuppressFloatingMenu }),
    [suppressFloatingMenu]
  );
  return <FloatingChromeContext.Provider value={value}>{children}</FloatingChromeContext.Provider>;
}

export function useFloatingChrome(): FloatingChromeContextValue {
  return React.useContext(FloatingChromeContext);
}

/**
 * Component-friendly helper: while the calling component is mounted,
 * sets `suppressFloatingMenu` to `true`; restores `false` on unmount.
 *
 * Use this from the camera-scan step (and any other future full-bleed
 * step that wants to hide the floating menu) so individual call sites
 * don't have to repeat the mount/unmount effect plumbing.
 */
export function useSuppressFloatingMenu(active: boolean = true): void {
  const { setSuppressFloatingMenu } = useFloatingChrome();
  React.useEffect(() => {
    if (!active) return;
    setSuppressFloatingMenu(true);
    return () => {
      setSuppressFloatingMenu(false);
    };
  }, [active, setSuppressFloatingMenu]);
}
